import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useAdRotation, AdMedia } from "@/components/AdSlot";
import { trackEvent } from "@/hooks/useArticleAnalytics";

/* Right-rail module 4 (§28-31): PERMANENT sponsored slot.
   Pulls live campaigns from the NewsLayer ad system (house fallback included),
   with a clear SPONSORED disclosure readable by assistive technology. */
export const SponsoredRailAd = () => {
  const { ad, hidden } = useAdRotation("sidebar", false);
  const ref = useRef<HTMLElement | null>(null);
  const counted = useRef<Set<string>>(new Set());
  const adId = ad?.id;

  useEffect(() => {
    const el = ref.current;
    if (!adId || !el) return;
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting && !counted.current.has(adId)) {
          counted.current.add(adId);
          api.post(`/ads/${adId}/impression`).catch(() => {});
          trackEvent("right_rail_ad_impression", { ad_id: adId });
          io.disconnect();
        }
      }
    }, { threshold: 0.5 });
    io.observe(el);
    return () => io.disconnect();
  }, [adId]);

  if (hidden || !ad) return null;
  const click = () => {
    api.post(`/ads/${ad.id}/click`).catch(() => {});
    trackEvent("right_rail_ad_click", { ad_id: ad.id });
  };
  const external = String(ad.target_url || "").startsWith("http");
  const media = (
    <span className="block rounded-lg overflow-hidden border border-white/[0.07] bg-[#08080a]">
      <AdMedia ad={ad} className="w-full h-auto max-h-[420px] object-contain block" />
    </span>
  );

  return (
    <section ref={ref} data-testid="sponsored-rail-ad" aria-label={`Sponsored content from ${ad.advertiser || "advertiser"}`}
      className="rounded-xl border border-white/[0.08] bg-[#0a0b10] p-4">
      <div className="flex items-center justify-between">
        <p className="font-mono text-[9px] font-bold uppercase tracking-[0.3em] text-gray-500">Sponsored</p>
        <span className="font-mono text-[8px] text-gray-600 uppercase tracking-widest border border-white/[0.12] rounded px-1.5 py-0.5">Ad</span>
      </div>
      <div className="mt-3">
        {external ? (
          <a href={ad.target_url} target="_blank" rel="noopener sponsored" onClick={click}
            data-testid="sponsored-ad-link" className="block">{media}</a>
        ) : (
          <Link to={ad.target_url || "/advertise"} onClick={click}
            data-testid="sponsored-ad-link" className="block">{media}</Link>
        )}
      </div>
      {ad.advertiser && (
        <div className="mt-3 flex items-center justify-between gap-3">
          <span className="font-mono text-[10px] text-gray-400 uppercase tracking-widest truncate">{ad.advertiser}</span>
          {external ? (
            <a href={ad.target_url} target="_blank" rel="noopener sponsored" onClick={click} data-testid="sponsored-ad-cta"
              className="inline-flex items-center gap-1 font-mono text-[10px] font-bold uppercase tracking-wider text-[#00E5FF] hover:text-white transition-colors shrink-0">
              Visit <ArrowRight className="h-3 w-3" />
            </a>
          ) : (
            <Link to={ad.target_url || "/advertise"} onClick={click} data-testid="sponsored-ad-cta"
              className="inline-flex items-center gap-1 font-mono text-[10px] font-bold uppercase tracking-wider text-[#00E5FF] hover:text-white transition-colors shrink-0">
              Learn more <ArrowRight className="h-3 w-3" />
            </Link>
          )}
        </div>
      )}
    </section>
  );
};
